/**
 * Channel policy — six rows, always, WP-W11. `web/shapes/outreach.md` §3 is the
 * agreed shape. Rendered only for a session holding `workforce.manage_policies`
 * (`OutreachScreen` decides that — never the authorisation, which the server still
 * runs on every call).
 *
 * `GET /outreach/channel-policy` always answers with exactly six rows, one per
 * `OutreachChannel`, so this panel never renders `EmptyState` for it — an
 * unconfigured company is six rows reading disabled, not zero rows. Each row is its
 * own small edit form and its own `POST /outreach/channel-policy` (a full replace);
 * `ChannelPolicyRow` is keyed by channel so remounting on a fresh read re-seeds its
 * local edit state from the freshly loaded values rather than carrying stale ones
 * across a reload.
 *
 * `requires_approval` is locked to checked and disabled on every channel but
 * `email` — the control communicates the server's own CHECK (`openapi.yaml`:
 * "forced to `true` server-side for every channel except `email`") rather than
 * offering a checkbox the next request would silently ignore.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  ChannelPolicy,
  ChannelPolicyUpsertRequest,
  OutreachChannel,
} from '../../api/outreach';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { isHighRiskOutreachChannel, OUTREACH_CHANNEL_LABEL } from '../../recruiting/outreach';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { SEVERITY_ICON } from '../../states/icons';

export interface ChannelPolicyPanelProps {
  readonly status: QueryStatus;
  readonly policies: readonly ChannelPolicy[] | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  /** The channel a save is in flight for, or the one that last refused — attributed per row. */
  readonly savingChannel: OutreachChannel | null;
  readonly savePending: boolean;
  readonly saveError: WorkforceApiError | null;
  readonly saved: ChannelPolicy | null;
  readonly onSave: (body: ChannelPolicyUpsertRequest) => void;
  readonly locale: string;
  readonly timezone: string;
}

interface RowProps {
  readonly policy: ChannelPolicy;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly saved: ChannelPolicy | null;
  readonly onSave: (body: ChannelPolicyUpsertRequest) => void;
  readonly locale: string;
  readonly timezone: string;
}

function ChannelPolicyRow({
  policy,
  pending,
  error,
  saved,
  onSave,
  locale,
  timezone,
}: RowProps): ReactNode {
  const { t } = useI18n();
  const highRisk = isHighRiskOutreachChannel(policy.channel);
  const [enabled, setEnabled] = useState(policy.enabled);
  const [paused, setPaused] = useState(policy.paused);
  const [dailyRateLimit, setDailyRateLimit] = useState(String(policy.daily_rate_limit));
  const [requiresApproval, setRequiresApproval] = useState(policy.requires_approval);

  const limitValue = Number(dailyRateLimit);
  const limitValid =
    dailyRateLimit.trim() !== '' && Number.isInteger(limitValue) && limitValue >= 0;

  return (
    <li className="wp-row" data-testid={`w11-policy-${policy.channel}`}>
      <div className="wp-row__main">
        <p>
          <Badge
            severity={policy.enabled ? 'low' : 'info'}
            icon={SEVERITY_ICON[policy.enabled ? 'low' : 'info']}
            label={t(policy.enabled ? 'outreach.policy.enabled' : 'outreach.policy.disabled')}
            testId={`w11-policy-${policy.channel}-enabled-badge`}
          />{' '}
          <span data-testid={`w11-policy-${policy.channel}-channel`}>
            {t(OUTREACH_CHANNEL_LABEL[policy.channel])}
          </span>
        </p>
        <p className="wp-small wp-muted" data-testid={`w11-policy-${policy.channel}-meta`}>
          {t('outreach.policy.meta', {
            paused: t(policy.paused ? 'outreach.policy.paused' : 'outreach.policy.active'),
            limit: policy.daily_rate_limit,
            approval: t(
              policy.requires_approval
                ? 'outreach.policy.approvalRequired'
                : 'outreach.policy.approvalNotRequired',
            ),
          })}
        </p>
        <p className="wp-small wp-muted">
          {policy.updated_at === null
            ? t('outreach.policy.neverConfigured')
            : t('outreach.policy.updatedAt', {
                when: formatInstant(policy.updated_at, locale, timezone),
              })}
        </p>

        {highRisk ? (
          <StatusNote
            severity="high"
            title={t('outreach.policy.highRisk.title')}
            testId={`w11-policy-${policy.channel}-warning`}
          >
            <p>{t('outreach.policy.highRisk.body')}</p>
          </StatusNote>
        ) : null}

        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (!limitValid || pending) return;
            onSave({
              channel: policy.channel,
              enabled,
              paused,
              daily_rate_limit: limitValue,
              requires_approval: highRisk ? true : requiresApproval,
            });
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor={`w11-policy-${policy.channel}-enabled`}>
              <input
                id={`w11-policy-${policy.channel}-enabled`}
                data-testid={`w11-policy-${policy.channel}-enabled-input`}
                type="checkbox"
                checked={enabled}
                onChange={(event) => {
                  setEnabled(event.target.checked);
                }}
              />{' '}
              {t('outreach.policy.form.enabled')}
            </label>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor={`w11-policy-${policy.channel}-paused`}>
              <input
                id={`w11-policy-${policy.channel}-paused`}
                data-testid={`w11-policy-${policy.channel}-paused-input`}
                type="checkbox"
                checked={paused}
                onChange={(event) => {
                  setPaused(event.target.checked);
                }}
              />{' '}
              {t('outreach.policy.form.paused')}
            </label>
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor={`w11-policy-${policy.channel}-limit`}>
              {t('outreach.policy.form.dailyRateLimit')}
            </label>
            <input
              className="wp-field__input"
              id={`w11-policy-${policy.channel}-limit`}
              data-testid={`w11-policy-${policy.channel}-limit-input`}
              type="number"
              min="0"
              max="1000000"
              value={dailyRateLimit}
              onChange={(event) => {
                setDailyRateLimit(event.target.value);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor={`w11-policy-${policy.channel}-approval`}>
              <input
                id={`w11-policy-${policy.channel}-approval`}
                data-testid={`w11-policy-${policy.channel}-approval-input`}
                type="checkbox"
                checked={highRisk ? true : requiresApproval}
                disabled={highRisk}
                onChange={(event) => {
                  setRequiresApproval(event.target.checked);
                }}
              />{' '}
              {t('outreach.policy.form.requiresApproval')}
            </label>
            {highRisk ? (
              <p className="wp-small wp-muted">
                {t('outreach.policy.form.requiresApprovalLocked')}
              </p>
            ) : null}
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid={`w11-policy-${policy.channel}-save`}
              disabled={!limitValid || pending}
            >
              {pending ? t('outreach.policy.form.saving') : t('outreach.policy.form.save')}
            </button>
          </div>
        </form>

        {error === null ? null : (
          <ApiErrorNote
            error={error}
            subject={t('outreach.policy.subjectWord')}
            testId={`w11-policy-${policy.channel}-error`}
          />
        )}
        {saved === null ? null : (
          <StatusNote
            severity="low"
            title={t('outreach.policy.form.ok')}
            testId={`w11-policy-${policy.channel}-ok`}
          />
        )}
      </div>
    </li>
  );
}

export function ChannelPolicyPanel(props: ChannelPolicyPanelProps): ReactNode {
  const { t } = useI18n();

  return (
    <section className="wp-section" aria-labelledby="w11-policy-heading" data-testid="w11-policy">
      <h2 className="wp-section__title" id="w11-policy-heading">
        {t('outreach.policy.title')}
      </h2>
      <p className="wp-small wp-muted">{t('outreach.policy.intro')}</p>
      <p className="wp-small wp-muted" data-testid="w11-policy-default-note">
        {t('outreach.policy.defaultNote')}
      </p>

      {props.status === 'loading' ? (
        <Skeleton
          rows={6}
          variant="block"
          label={t('outreach.policy.subjectWord')}
          testId="w11-policy-skeleton"
        />
      ) : null}
      {props.status === 'error' && props.error !== null ? (
        <ApiErrorNote
          error={props.error}
          onRetry={props.onReload}
          subject={t('outreach.policy.subjectWord')}
          testId="w11-policy-list-error"
        />
      ) : null}

      {props.policies === null ? null : (
        <ul className="wp-rows" data-testid="w11-policy-list">
          {props.policies.map((policy) => (
            <ChannelPolicyRow
              key={policy.channel}
              policy={policy}
              pending={props.savingChannel === policy.channel && props.savePending}
              error={props.savingChannel === policy.channel ? props.saveError : null}
              saved={props.savingChannel === policy.channel ? props.saved : null}
              onSave={props.onSave}
              locale={props.locale}
              timezone={props.timezone}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
